import { useRef, useState } from 'react';
import './style.scss';
import { TranslationService } from '../../services/translation.service';
import { useTexts } from '../../hooks/texts.hook';
import LocationAutocomplete, { type ILocationAutocompleteSelection, type ILocationAutocompleteState } from '../Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../../models/location-type.enum';
import { type IAutocomplete } from '../Shared/AutocompleteBase';
import { AutocompleteLabelPosition } from '../Shared/AutocompleteBase/autocomplete-base.enum';
import DepartureBoardLinkHelper from '../../helpers/departure-board-link.helper';
import ValidationInfo from '../../validation/validation-info';
import { StopAreaApiModel } from '../StopAreas/models/stop-area.api-model';
import { IStopArea } from '../../models/stop-area.model';
import { IStopPoint } from '../../models/stop-point.model';
import LogService from '../../services/log-service/log.service';
import Alert from '../Shared/Alert';

interface ILocationViewModel {
  id: string;
  stopArea: IStopArea;
  selectedStopPoints: IStopPoint[];
}

function getGidsFromSelectedStopArea(selectedStopArea: ILocationViewModel | undefined): string[] | undefined {
  if (!selectedStopArea) return undefined;
  return selectedStopArea.selectedStopPoints.length > 0 ? selectedStopArea.selectedStopPoints.map((sp) => sp.gid) : [selectedStopArea.stopArea.gid];
}

function getUrlFromStopAreas(selectedStopAreas: ILocationViewModel[]): string | undefined {
  return DepartureBoardLinkHelper.generateLink(
    selectedStopAreas.length > 0 ? getGidsFromSelectedStopArea(selectedStopAreas[0]) : undefined,
    selectedStopAreas.length > 1 ? getGidsFromSelectedStopArea(selectedStopAreas[1]) : undefined,
    'vasttrafikse-departureboardlinkgenerator',
  );
}

function DepartureBoardLinkGenerator() {
  const { t } = useTexts();

  // === STATE ===
  const [selectedStopAreas, setSelectedStopAreas] = useState<ILocationViewModel[]>([]);
  const [locationAutocompleteState, setLocationAutocompleteState] = useState<ILocationAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });
  const [showError, setShowError] = useState(false);

  // === REFS ===
  const locationAutocompleteRef = useRef<IAutocomplete>(null);

  const url = selectedStopAreas.length > 0 ? getUrlFromStopAreas(selectedStopAreas) : undefined;

  async function handleLocationSelect(item: ILocationAutocompleteSelection | undefined): Promise<void> {
    if (item === undefined || item.type !== LocationAutocompleteSelectionType.location) return;
    if (item.location && item.location.gid && item.location.name) {
      try {
        const response = await fetch(`/api/stop-area-search/stopareas/${item.location.gid}`);
        if (!response.ok) {
          throw new Error(`Error getting stop area, status: ${response.status}`);
        }
        const stopAreas = StopAreaApiModel.mapToBusinessModel(await response.json());
        if (!stopAreas) {
          throw new Error('Failed to map stop areas data');
        }
        setSelectedStopAreas((current) => [
          ...current,
          {
            stopArea: stopAreas,
            selectedStopPoints: [],
            id: `${stopAreas.gid}_${Date.now()}`,
          },
        ]);
        setLocationAutocompleteState({
          value: undefined,
          validationInfo: new ValidationInfo(),
        });
        setShowError(false);
      } catch (error) {
        LogService.logError(error, 'Error fetching stop areas');
        setShowError(true);
      }
    }
    locationAutocompleteRef.current?.clear();
  }

  function handleStopAreaRemove(id: string) {
    setSelectedStopAreas(selectedStopAreas.filter((x) => x.id !== id));
  }

  function handleClearClick() {
    setSelectedStopAreas([]);
    setShowError(false);
  }

  return (
    <div className="departure-board-link-generator">
      <div className="mb-3">
        <LocationAutocomplete
          /* @ts-expect-error ts(2322) */
          ref={locationAutocompleteRef}
          id="location-autocomplete"
          labelText={TranslationService.get('departureboardlinkgeneratorpage.stoplabel')}
          labelClassName="form-label"
          labelPosition={AutocompleteLabelPosition.above}
          placeholderText={TranslationService.get('departureboardlinkgeneratorpage.searchtitle')}
          disabled={selectedStopAreas.length > 1}
          listAriaLabelledBy="departure-board-link-generator-label"
          locationTypes={[LocationType.stopArea]}
          enableCurrentPosition={false}
          state={locationAutocompleteState}
          onSelect={handleLocationSelect}
        />
      </div>

      {showError && <Alert show={true} heading={t.generalError} closable={false} className="mb-2" />}

      {selectedStopAreas.length > 0 && (
        <div className={selectedStopAreas.length > 0 ? 'mb-4' : ''}>
          <div className="mb-2 d-flex align-items-center">
            <span className="h4 mb-0" id="selected-stations">{`${TranslationService.get('departureboardlinkgeneratorpage.selectedstations')} (${selectedStopAreas.length}/2)`}</span>
            <button className="btn btn-tertiary btn-icon-left btn-icon-xmark btn-tertiary-position-right text-nowrap ms-auto" onClick={handleClearClick} aria-describedby="selected-stations">
              {TranslationService.get('departureboardlinkgeneratorpage.removefilter')}
            </button>
          </div>
          <div className="w-100 d-flex flex-column gap-2">
            {selectedStopAreas.map((stopArea) => (
              <div key={stopArea.id} className="p-3 d-flex rounded flex-column stop-area-card gap-2" role="group" aria-labelledby={`${stopArea.id}-name`}>
                <div className="d-flex flex-row align-items-center gap-2">
                  <span id={`${stopArea.id}-name`} className="fw-semibold stop-area-name">
                    {stopArea.stopArea.name}
                  </span>
                  <button
                    className="btn btn-tertiary btn-icon-left btn-icon-trash btn-tertiary-position-right departure-board-link-generator__remove-button flex-shrink-0 text-nowrap ms-auto"
                    onClick={() => handleStopAreaRemove(stopArea.id)}
                    aria-label={`${TranslationService.get('departureboardlinkgeneratorpage.remove')} ${stopArea.stopArea.name}`}
                  >
                    {TranslationService.get('departureboardlinkgeneratorpage.remove')}
                  </button>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <label className="fw-normal" htmlFor={`selectId${stopArea.id}`}>
                    {TranslationService.get('departureboardlinkgeneratorpage.stoppoints') + ': '}
                  </label>
                  <select
                    id={`selectId${stopArea.id}`}
                    className="form-select stop-point-select"
                    value={stopArea.selectedStopPoints.length === stopArea.stopArea.stopPoints.length ? 'all' : stopArea.selectedStopPoints[0]?.gid}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setSelectedStopAreas((current) =>
                        current.map((sa) =>
                          sa.id === stopArea.id
                            ? {
                                ...sa,
                                selectedStopPoints: selectedValue === 'all' ? [] : sa.stopArea.stopPoints.filter((sp) => sp.gid === selectedValue),
                              }
                            : sa,
                        ),
                      );
                    }}
                  >
                    <option
                      value="all"
                      aria-label={`${TranslationService.get('departureboardlinkgeneratorpage.stoppointsfor')} ${stopArea.stopArea.name}: ${TranslationService.get(
                        'departureboardlinkgeneratorpage.allstoppoints',
                      )}`}
                    >
                      {TranslationService.get('departureboardlinkgeneratorpage.allstoppoints')}
                    </option>
                    {stopArea.stopArea.stopPoints
                      .sort((a, b) => (a.platform ?? '').localeCompare(b.platform ?? ''))
                      .map((stopPoint) => (
                        <option
                          key={stopPoint.gid}
                          value={stopPoint.gid}
                          aria-label={`${TranslationService.get('departureboardlinkgeneratorpage.stoppointsfor')} ${stopArea.stopArea.name}: ${TranslationService.get(
                            'departureboardlinkgeneratorpage.stoppoint',
                          )} ${stopPoint.platform}`}
                        >
                          {TranslationService.get('departureboardlinkgeneratorpage.stoppoint') + ` ${stopPoint.platform}`}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedStopAreas.length > 0 && (
        <div className="d-grid">
          <button
            className="btn btn-primary btn-icon-right btn-icon-up-right-from-square"
            onClick={() => url && window.open(url, '_blank')}
            disabled={!url}
            aria-label={`${TranslationService.get('departureboardlinkgeneratorpage.showdepartureboard')} ${TranslationService.get('departureboardlinkgeneratorpage.for')} ${selectedStopAreas
              .map((sa) => sa.stopArea.name)
              .join(` ${TranslationService.get('common.and')} `)} ${TranslationService.get('departureboardlinkgeneratorpage.innewtab')}`}
          >
            <span>{TranslationService.get('departureboardlinkgeneratorpage.showdepartureboard')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default DepartureBoardLinkGenerator;
